import React, { useEffect, useState } from 'react';
import { adminAPI } from '@/services/api';
import { Users, Briefcase, ClipboardList, Building2, UserPlus, TrendingUp } from 'lucide-react';

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getStats().then(setStats).catch(() => { /* noop */ }).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-slate-500 dark:text-zinc-500">Loading dashboard...</p>;
  if (!stats) return <p className="text-red-600 dark:text-red-400">Failed to load stats</p>;

  return (
    <div data-testid="admin-home">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Platform Overview</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-8">Monitor your platform metrics</p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Users} label="Total Users" value={stats.total_users} color="blue" />
        <StatCard icon={UserPlus} label="Applicants" value={stats.total_applicants} color="emerald" />
        <StatCard icon={Building2} label="Recruiters" value={stats.total_recruiters} color="purple" />
        <StatCard icon={Briefcase} label="Total Jobs" value={stats.total_jobs} color="amber" />
        <StatCard icon={ClipboardList} label="Screenings" value={stats.total_screenings} color="rose" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5">
          <h3 className="text-slate-900 dark:text-white font-medium mb-4">Quick Stats</h3>
          <div className="space-y-3">
            <StatRow label="Active Jobs" value={stats.active_jobs} />
            <StatRow label="Completed Screenings" value={stats.completed_screenings} />
            <StatRow label="Total Matches" value={stats.total_matches} />
            <StatRow label="Organizations" value={stats.total_orgs} />
          </div>
        </div>

        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-5">
          <h3 className="text-slate-900 dark:text-white font-medium mb-4">Recent Signups</h3>
          {stats.recent_signups?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_signups.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-200 dark:border-zinc-800/30 last:border-0">
                  <div>
                    <p className="text-slate-600 dark:text-zinc-300 text-sm">{u.email}</p>
                    <p className="text-slate-500 dark:text-zinc-600 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                    u.role === 'applicant' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                    u.role === 'recruiter' ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400' :
                    'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'
                  }`}>{u.role}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-slate-500 dark:text-zinc-500 text-sm">No recent signups</p>}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10',
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-500/10',
    amber: 'text-amber-600 dark:text-amber-400 bg-amber-500/10',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-500/10',
    rose: 'text-rose-600 dark:text-rose-400 bg-rose-500/10',
  };
  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${colors[color]}`}><Icon className="w-4 h-4" /></div>
        <div><p className="text-slate-500 dark:text-zinc-500 text-xs">{label}</p><p className="text-slate-900 dark:text-white font-semibold text-lg">{value}</p></div>
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500 dark:text-zinc-500">{label}</span>
      <span className="text-slate-900 dark:text-white font-medium">{value}</span>
    </div>
  );
}
